import type { RuntimeConfig } from "./config";

export type Session = {
  idToken: string;
  accessToken: string;
  expiresAt: number;
  claims: Record<string, unknown>;
};

const SESSION_KEY = "food-drive.session";
const VERIFIER_KEY = "food-drive.pkce.verifier";

function base64Url(bytes: ArrayBuffer | Uint8Array) {
  const array = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = "";
  for (const byte of array) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomVerifier() {
  const bytes = crypto.getRandomValues(new Uint8Array(48));
  return base64Url(bytes);
}

function decodeClaims(token: string): Record<string, unknown> {
  const payload = token.split(".")[1];
  if (!payload) return {};
  const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
  return JSON.parse(decodeURIComponent(escape(atob(normalized))));
}

export function getSession(): Session | null {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as Session;
    if (Date.now() >= session.expiresAt) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function getGroups(session: Session | null): string[] {
  const value = session?.claims["cognito:groups"];
  if (Array.isArray(value)) return value.map(String);
  if (typeof value !== "string") return [];
  const trimmed = value.trim();
  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {
      // Cognito/API Gateway can emit bracketed group names without JSON quotes.
    }
  }
  const unwrapped = trimmed.startsWith("[") && trimmed.endsWith("]")
    ? trimmed.slice(1, -1)
    : trimmed;
  return unwrapped
    .split(",")
    .map((group) => group.trim().replace(/^["']|["']$/g, ""))
    .filter(Boolean);
}

export async function beginLogin(config: RuntimeConfig) {
  if (!config.userPoolClientId || !config.cognitoDomain) {
    throw new Error("Staff sign-in is available after the AWS deployment is configured.");
  }
  const verifier = randomVerifier();
  const challenge = base64Url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)));
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  const redirectUri = `${window.location.origin}/callback`;
  const params = new URLSearchParams({
    client_id: config.userPoolClientId,
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: redirectUri,
    code_challenge_method: "S256",
    code_challenge: challenge,
  });
  window.location.assign(`${config.cognitoDomain}/oauth2/authorize?${params}`);
}

export async function finishLogin(config: RuntimeConfig, code: string): Promise<Session> {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error("The sign-in session expired. Please try again.");
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.userPoolClientId,
    code,
    redirect_uri: `${window.location.origin}/callback`,
    code_verifier: verifier,
  });
  const response = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!response.ok) throw new Error("Unable to complete staff sign-in.");
  const tokens = await response.json();
  const session: Session = {
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
    expiresAt: Date.now() + Number(tokens.expires_in || 3600) * 1000,
    claims: decodeClaims(tokens.id_token),
  };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  sessionStorage.removeItem(VERIFIER_KEY);
  return session;
}

export function signOut(config: RuntimeConfig) {
  sessionStorage.removeItem(SESSION_KEY);
  const params = new URLSearchParams({
    client_id: config.userPoolClientId,
    logout_uri: `${window.location.origin}/`,
  });
  window.location.assign(`${config.cognitoDomain}/logout?${params}`);
}
